import { RangeRef, NodeEntry } from 'domains/reporter/RichTextEditor/core';
import type { ReportStatuses } from 'domains/reporter/Reporter/state';
import {
  VOICE_COMMAND_TYPES_EDITOR,
  type VoiceCommandsEditor,
  type NvoqMessage,
  type ParsedDictationChunk,
  type ParsedDictationChunkSubstitutionEditor,
  type ParsedDictationChunkSubstitutionEditorNextNumberDynamic,
  type ParsedDictationChunkText,
} from './NvoqQueue/createNvoqWebsocketFactory';
import type { Step, StepEntry } from './NvoqQueue/useSteps';
import type { OnExternalSubstitution, WebsocketCreationOptions } from './types';

import { isSquareBracketType } from '../../constants';
import { useRangeMasksDispatch } from '../../hooks/useRangeMasks';
import { getEditorDictationSelectionSafe, getRangeRefSafe, partialEditor } from '../../utils';
import { createEditorLog } from '../../utils/logging';
import { stringifyRange } from '../../utils/stringify';
import { isCursorAfterFinalHeadingColon } from '../heading/utils/normalization';
import { getCurrentActiveListVariant } from '../list/utils';
import type { CurrentActiveListVariantState } from '../list/hooks/useCurrentList';
import { LINE_BREAK_PLUGIN_ID } from '../lineBreak/types';
import { getOrInsertNextInlineParagraph } from '../paragraph/utils';
import {
  getDeferredSteps,
  handleStableText,
  stepEntriesTextToString,
  useSteps,
} from './NvoqQueue/useSteps';
import { autoCorrectNvoqStableText } from './NvoqQueue/utils/autoCorrectNvoqStableText';
import { createGroupedStepEntries } from './NvoqQueue/utils/createGroupedStepEntries';
import { generateNextParagraphInListSironaSubstitutionForNvoq } from './NvoqQueue/utils/generateNextParagraphInListSironaSubstitution';
import { maybeDoVoiceCommand } from './NvoqQueue/utils/maybeDoVoiceCommand';
import { createDictationChunksFromNvoqStableText } from './NvoqQueue/utils/parseNvoqStableText';
import { trimHypothesisText } from './NvoqQueue/utils/trimHypothesisText';
import { normalizeDecimalSpacing } from './NvoqQueue/utils/digitAndDecimalUtils';
import { SIRONA_SUBSTITUTION_REGEX } from './NvoqQueue/utils/utils';
import { MANUAL_CORRECTION_WORDS_LIMIT, STOPPED_DICTATION_STALE_GRACE_MS } from './constants';
import { DICTATION_PLUGIN_ID } from './types';
import { usePicklistDictationSelection } from './usePicklistDictationSelection';
import { useTriggerlessPicklistState } from './useTriggerlessPicklistState';
import {
  getDictationSequenceRank,
  getMarkerEndMs,
  getMarkerStartMs,
  getSortedDictationFamilyEntries,
  isNumericSegmentForBaseId,
  resolveDictationFamilyId,
  wordCount,
} from './utils';
import { usePicklistEditor } from '../../../Template/usePicklist';
import { getPicklistEndPointById } from '../picklist/utils';
import { getActivePicklist, getParagraphRangeAtPoint } from '../../utils/selection';
import { PicklistPluginElement } from '../../slate-custom-types';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { getNavigationItemsFromEditor } from 'domains/reporter/Reporter/Outline/utils';
import { REPORT_EDITABLE_STATUSES, reportStatusState } from 'domains/reporter/Reporter/state';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useImpressionGenerator } from 'hooks/useImpressionGenerator';
import { RICH_TEXT_EDITOR, useMostRecentInput } from 'hooks/useMostRecentInput';
import { useReporterDictationStepToast } from 'hooks/useReporterDictationStepToast';
import { useWorklistItemAnalytics } from 'hooks/useWorklistItemAnalytics';
import analytics from 'modules/analytics';
import { reporter } from 'modules/analytics/constants';
import { logger } from 'modules/logger';
import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { Editor, type EditorRangeRefOptions, Element, Node, Point, Range } from 'slate';
import { useRequiredFieldIndicator } from 'hooks/useRequiredFieldIndicator';
import { useFocusMode, FocusModeOperation } from 'hooks/useFocusMode';
import { slateSource } from 'domains/reporter/RichTextEditor/core/slateSource';
import Deferred from 'utils/deferred';
import { v7 as uuidv7 } from 'uuid';
import {
  measureHypothesisTextPerformance,
  measureStableTextPerformance,
} from '../../analytics/performance';
import { getClosestEntryId, getEntryIdForOnset, isTrailingPunctuationText } from './ASRPlex/utils';
import { WithApolloContext } from 'modules/Apollo/types';
import { useWebSpeechPicklistAdvisor } from './useWebSpeechPicklistAdvisor';
import { useHeadingKeywords } from 'hooks/useHeadingKeywords';
import { buildHeadingLabelSet } from '../headingDecoration/matcher';
import { isLabelColon } from '../../stitching/colonCase';

type UseTextProcessingProps = WithApolloContext & {
  editor: Editor;
  enablePicklistDictation: boolean;
  onExternalSubstitution: OnExternalSubstitution;
  isAlwaysEditable?: boolean;
  onDictationFinalized?: (ids: string[]) => void;
};

export type DictationStableTextManagerMapValue = {
  selection: Range;
  selectionRef: RangeRef;
  textDone: boolean;
  deferred: Deferred<void>;
  messageQueue: Map<string, Deferred<void>>;
  dictationIndex: number;
  audioOffsetMs?: number;
  stoppedAtMs: number | null;
  // track picklist id for option by choice name functionality
  picklistId?: string | null;
  // snapshot triggerless eligibility when this dictation started so immediate navigation
  // does not revoke option-name selection for the in-flight utterance
  isTriggerlessPicklistChoiceAllowed?: boolean;
  // Once ASR_PLEX emits a real <srna> substitution, Web Speech advisor text may still contain the spoken
  // command words; later stable-text packets should not reuse that stale advisor snapshot.
  hasSeenStableTextSubstitution?: boolean;
};

const replaceSelectionRef = (
  editor: Editor,
  entry: Pick<DictationStableTextManagerMapValue, 'selectionRef'>,
  range: Range,
  options?: EditorRangeRefOptions
): void => {
  entry.selectionRef.unref();
  entry.selectionRef = Editor.rangeRef(editor, range, options);
};

function getContextualLeadingTextStep({
  editor,
  at,
  message,
  formattedMessage,
  sourceChunk,
  dictionary,
  headingLabelSet,
  variant,
}: {
  editor: Editor;
  at: Range;
  message: NvoqMessage;
  formattedMessage: NvoqMessage;
  sourceChunk: ParsedDictationChunkText;
  dictionary: Record<string, string>;
  headingLabelSet: Set<string>;
  variant: CurrentActiveListVariantState;
}): StepEntry | null {
  const insertionStart = Range.start(at);
  const paragraphRange = getParagraphRangeAtPoint(editor, insertionStart);
  if (paragraphRange == null) return null;
  const beforeRange = { anchor: paragraphRange.anchor, focus: insertionStart };
  const lastLineBreak = Editor.nodes(editor, {
    at: beforeRange,
    match: (node) => Element.isElement(node) && node.type === LINE_BREAK_PLUGIN_ID,
    reverse: true,
  }).next().value;
  if (lastLineBreak != null) beforeRange.anchor = Editor.end(editor, lastLineBreak[1]);
  const textBefore = Editor.string(editor, beforeRange);
  const prefix = textBefore.trimEnd();
  if (prefix === '') return null;
  if (prefix.endsWith(':')) {
    const block = Editor.above(editor, {
      at: insertionStart,
      match: (node) => Element.isElement(node) && Editor.isBlock(editor, node),
    })?.[0];
    if (
      block != null &&
      Element.isElement(block) &&
      isLabelColon(block, prefix, prefix.length - 1, { headingLabelSet })
    )
      return null;
  }
  const contextualMessage = generateNextParagraphInListSironaSubstitutionForNvoq(
    autoCorrectNvoqStableText(message, dictionary, { textBefore }),
    variant
  );
  if (contextualMessage.data.text === formattedMessage.data.text) return null;

  // Only replace leading report text. Commands and their parsed arguments keep baseline semantics.
  const originalParts = formattedMessage.data.text.split(SIRONA_SUBSTITUTION_REGEX);
  const contextualParts = contextualMessage.data.text.split(SIRONA_SUBSTITUTION_REGEX);
  if (
    originalParts.length !== contextualParts.length ||
    originalParts.some((part, index) => index % 2 === 1 && part !== contextualParts[index])
  )
    return null;
  const originalText = normalizeDecimalSpacing(originalParts[0]);
  const literalText = normalizeDecimalSpacing(sourceChunk.payload.data.text);
  const offset = originalText.indexOf(literalText);
  if (literalText === '' || offset < 0 || originalText.slice(0, offset).trim() !== '') return null;
  const suffix = originalText.slice(offset + literalText.length);
  const contextualText = normalizeDecimalSpacing(contextualParts[0]);
  if (!contextualText.endsWith(suffix)) return null;
  const text = contextualText.slice(0, contextualText.length - suffix.length);
  return handleStableText({
    ...sourceChunk,
    payload: {
      ...sourceChunk.payload,
      data: { ...sourceChunk.payload.data, text, substitutedText: text },
    },
  });
}

enum FinalizeDictationReason {
  TextDone = 'text_done',
  StaleSuperseded = 'stale_superseded',
}

/**
 * Updates dictation.picklistId from the active picklist at the current (or given) editor selection.
 * Use after navigation (NextBracket, PreviousBracket, GoToField) or InsertMacro so the next
 * PicklistOptionSelection in the same chunk targets the correct field.
 */
function refreshDictationPicklistContext(
  editor: Editor,
  dictation: DictationStableTextManagerMapValue,
  selection?: Range | null
): void {
  const sel = selection ?? editor.selection;
  if (sel == null) return;
  const picklistEntry = getActivePicklist(editor, sel);
  dictation.picklistId =
    picklistEntry != null
      ? ((picklistEntry[0] as { picklistID?: string }).picklistID ?? null)
      : null;
}

const resolveOriginalTextStepEntry = (stepEntry: StepEntry): StepEntry => {
  const [, chunk] = stepEntry;
  if (chunk.type !== 'SUBSTITUTION' || chunk.payload.target !== 'EDITOR') {
    return stepEntry;
  }

  // PicklistOptionSelection can still execute against its original picklist even after
  // the selection has moved, so don't replace it with the fallback text.
  if (chunk.payload.action === 'PicklistOptionSelection') {
    return stepEntry;
  }

  // Deferred list/voice-command substitutions can carry the original text step.
  // When we flush/bypass deferral, prefer that text step so we insert exactly what was dictated.
  const payload = chunk.payload as { originalTextStepEntry?: StepEntry };
  const originalTextStepEntry = payload.originalTextStepEntry;

  return originalTextStepEntry ?? stepEntry;
};

export type RegisterDictationOptions = {
  selection: Range;
  audioOffsetMs?: number;
  localSequence?: number;
};

type SetShouldSkipRecorderCreationFn = (shouldSkip: boolean) => void;

type UseTextProcessingResult = {
  processStableTextSerially: (arg1: NvoqMessage, arg2: Range, arg3: string) => Promise<void>;
  processFocusMapAppend: (arg1: NvoqMessage, arg2: string[]) => Promise<void>;
  processFocusMapDelete: (arg1: string, arg2: string[]) => Promise<void>;
  processHypothesisText: (arg1: NvoqMessage, arg2: Range, arg3: string) => void;
  registerDictation: (id: string, options: RegisterDictationOptions) => void;
  flushDeferredStepsForSelection: (selection: Range, dictationId: string) => boolean;
  markDictationStopped: (id: string) => void;
  selectionToStepsQueueMap: {
    current: Map<Range, StepEntry[]>;
  };
  dictationStableTextManagerMap: {
    current: Map<string, DictationStableTextManagerMapValue>;
  };
  isMousePressed: boolean;
  setShouldSkipWebsocketCreationRef: MutableRefObject<Nullable<SetShouldSkipRecorderCreationFn>>;
  mostRecentStableTextInserted: {
    current: Nullable<string>;
  };
  skipWebsocketCreationOptionsRef: {
    current: Nullable<WebsocketCreationOptions>;
  };
};

/**
 * Custom hook that establishes state and returns functions for processing stable/hypothesis text.
 *
 * Abstracts logic originally closely tied to the NvoqQueue apparatus, so it can be used by both NvoqQueue and ASRPlex.
 */
export const useTextProcessing = ({
  editor,
  enablePicklistDictation,
  onExternalSubstitution,
  context,
  isAlwaysEditable = false,
  onDictationFinalized,
}: UseTextProcessingProps): UseTextProcessingResult => {
  const dictationStableTextManagerMap = useRef(
    new Map<string, DictationStableTextManagerMapValue>()
  );
  const {
    isTriggerlessAllowedForPicklist,
    consumeTriggerlessPicklistChoice,
    refreshTriggerlessPicklistStateFromSelection,
  } = useTriggerlessPicklistState({ editor });

  const { createStepsFromStableText, applySteps } = useSteps({ context });
  const analyticsData = useWorklistItemAnalytics({ context });
  const { data: meData } = useCurrentUser({ context });
  const headingKeywords = useHeadingKeywords({ context });
  const headingLabelSet = useMemo(() => buildHeadingLabelSet(headingKeywords), [headingKeywords]);
  const autoCorrectDictionary = useMemo(() => {
    const dictionary: Record<string, string> =
      meData?.me?.autoCorrect != null
        ? Object.fromEntries(meData.me.autoCorrect.map((acEntry) => [acEntry.key, acEntry.value]))
        : {};
    return dictionary;
  }, [meData]);

  const selectionToStepsQueueMap = useRef(new Map<Range, StepEntry[]>());
  const selectionRefToStepsQueueMap = useRef(new Map<RangeRef, StepEntry[]>());

  const { isMousePressed, mouseUnpressed, mostRecentInput, setMostRecentInput } =
    useMostRecentInput({ id: RICH_TEXT_EDITOR, context });
  const mousePressedSinceLastStableTextRender = useRef<Nullable<boolean>>();

  const [isReporterModifyDictationEnabled] = useFeatureFlagEnabled(FF.REPORTER_MODIFY_DICTATION);
  const [isReporterPicklistOptionByNameEnabled] = useFeatureFlagEnabled(
    FF.REPORTER_PICKLIST_OPTION_BY_NAME
  );
  const {
    startPicklistAdvisorSession,
    stopPicklistAdvisorSession,
    setPicklistAdvisorDeferred,
    getPicklistAdvisorTranscript,
  } = useWebSpeechPicklistAdvisor({
    enabled: isReporterPicklistOptionByNameEnabled,
  });

  // whenever the mouse is pressed, set the mousePressedSinceLastStableTextRender to true
  useEffect(() => {
    if (isMousePressed === true) {
      mousePressedSinceLastStableTextRender.current = true;
    }
  }, [isMousePressed]);

  // whenever the mostRecentInput changes and its not 'mouse', reset the mousePressedSinceLastStableTextRender
  useEffect(() => {
    if (mostRecentInput !== 'mouse' && !isMousePressed) {
      mousePressedSinceLastStableTextRender.current = false;
    }
  }, [mostRecentInput, isMousePressed]);

  const setShouldSkipWebsocketCreationRef = useRef<Nullable<SetShouldSkipRecorderCreationFn>>();
  const decorateDispatch = useRangeMasksDispatch();

  const removeDecorationForSelection = useCallback(
    (selection: Range) => {
      // Remove the decoration for the grand unveiling of the stable text!
      decorateDispatch((decorations) => decorations.filter((d) => d.selectionRef !== selection));
    },
    [decorateDispatch]
  );
  const cleanMessageQueueForSelection = useCallback(
    (selection: Range, isDone: boolean) => {
      logger.info('[dictation] Cleaning message queue for selection', {
        logId: stringifyRange(selection),
        editor: partialEditor,
        selectionToStepsQueueMap: JSON.stringify(selectionToStepsQueueMap.current),
      });
      removeDecorationForSelection(selection);
      if (isDone) {
        selectionToStepsQueueMap.current.delete(selection);
      } else {
        selectionToStepsQueueMap.current.set(selection, []);
      }
    },
    [removeDecorationForSelection]
  );

  const { insertSelectedPicklistItemByIndex } = usePicklistDictationSelection({ editor });
  const { getPicklistOptions } = usePicklistEditor();
  const ignoreMergeFieldsInNavigation =
    meData?.me?.reporterSettings?.mergeFieldsSettings?.ignoreNavigation ?? false;
  const { generateAndStitchImpression } = useImpressionGenerator();
  const { enqueueDictationStepToast } = useReporterDictationStepToast();
  const reportStatus = useRecoilValue<ReportStatuses>(reportStatusState);
  const mostRecentStableTextInserted: {
    current: Nullable<string>;
  } = useRef<Nullable<string>>();
  const { onInsertTextRequiredField } = useRequiredFieldIndicator();
  const { getInsertionPoint, deleteTextInSection, mostRecentFocusModeOperationRef } = useFocusMode({
    context,
  });

  const processFocusMapAppend = useCallback(
    async (msg: NvoqMessage, section: string[]) => {
      const focusModeSource = slateSource.ai.focusMode;
      const dictationChunks = createDictationChunksFromNvoqStableText(msg);
      const stepEntries = await createStepsFromStableText(dictationChunks, {
        fieldNames: [],
        enablePicklistDictation: false,
        source: focusModeSource,
      });

      const groupedStepEntries = createGroupedStepEntries(stepEntries);

      groupedStepEntries.forEach((stepEntryList: StepEntry[]) => {
        if (editor?.selection == null) {
          return;
        }

        const firstStepEntry: StepEntry = stepEntryList[0];
        const [firstStep, firstDictationChunk]: [Step, ParsedDictationChunk] = firstStepEntry;

        const shouldAttemptExternalVoiceCommand =
          firstStep.type === 'EXTERNAL_VOICE_COMMAND' &&
          firstDictationChunk.type === 'SUBSTITUTION' &&
          firstDictationChunk.payload.target !== 'EDITOR';

        if (shouldAttemptExternalVoiceCommand) {
          onExternalSubstitution({
            type: 'RecorderVoiceCommand',
            selection: editor.selection,
            substitution: firstDictationChunk.payload,
          });

          if (editor?.selection == null) {
            return;
          }

          if (firstDictationChunk.payload.action === 'InsertReportTemplate') {
            removeDecorationForSelection(editor.selection);
          }
        } else {
          const macroSteps = stepEntryList.filter(
            (
              stepEntry
            ): stepEntry is [Extract<Step, { type: 'INSERT_MACRO' }>, ParsedDictationChunk] =>
              stepEntry[0].type === 'INSERT_MACRO'
          );
          if (macroSteps.length > 0) {
            logger.info('[processFocusMapAppend] Focus mode macro insertion requested', {
              dictationId: msg.id,
              section,
              selection: stringifyRange(editor.selection),
              macroIds: macroSteps.map(([step]) => step.payload.smid),
              macroNames: macroSteps.map(([step]) => step.payload.name),
              stepTypes: stepEntryList.map(([step]) => step.type),
            });
          }

          // TODO applySteps requires a range but this is returning a Point
          const at = getInsertionPoint(editor, section);
          applySteps({
            editor,
            steps: stepEntryList,
            at: (at as unknown as Range) ?? Editor.end(editor, []),
            /**
             * If the at range includes the current editor selection, augment the editor selection,
             * otherwise leave it as is because that means they have tabbed to a future bracket
             * and this one is a previous queue item.
             *
             * NOTE: This function is named funky in Slate. See:
             * https://github.com/ianstormtaylor/slate/issues/4283
             */
            select: true,
            source: focusModeSource,
            dictationId: msg.id,
            dictationIndex: 0,
            autoCorrectDictionary,
          });
          if (macroSteps.length > 0) {
            logger.info('[processFocusMapAppend] Focus mode macro insertion applied', {
              dictationId: msg.id,
              section,
              insertionPoint: at,
              macroIds: macroSteps.map(([step]) => step.payload.smid),
            });
          }
          mostRecentFocusModeOperationRef.current = FocusModeOperation.Append;
        }
      });
    },
    [
      applySteps,
      autoCorrectDictionary,
      createStepsFromStableText,
      editor,
      getInsertionPoint,
      onExternalSubstitution,
      removeDecorationForSelection,
      mostRecentFocusModeOperationRef,
    ]
  );

  const processFocusMapDelete = useCallback(
    async (text: string, section: string[]) => {
      deleteTextInSection(editor, text, section);
    },
    [deleteTextInSection, editor]
  );

  const mostRecentSelectionRef = useRef<RangeRef>();

  const finalizeDictation = useCallback(
    (id: string, reason: FinalizeDictationReason) => {
      const baseId = resolveDictationFamilyId(
        id,
        dictationStableTextManagerMap.current,
        isReporterModifyDictationEnabled
      );
      const keysToDelete = new Set<string>();

      if (isReporterModifyDictationEnabled) {
        // With modify dictation enabled, a single logical dictation can be split into
        // multiple segment keys: baseId, baseId-1, baseId-2, ...
        // Collect the entire family so finalize removes them together.
        for (const key of Array.from(dictationStableTextManagerMap.current.keys())) {
          if (key === baseId) {
            keysToDelete.add(key);
            continue;
          }

          // Match only numeric sequence suffixes for this base id.
          if (isNumericSegmentForBaseId(key, baseId)) {
            keysToDelete.add(key);
          }
        }
      } else {
        keysToDelete.add(baseId);
      }

      const removedEntries: Array<{
        key: string;
        entry: DictationStableTextManagerMapValue;
      }> = [];
      for (const key of keysToDelete) {
        const entry = dictationStableTextManagerMap.current.get(key);
        // Entry can already be missing due to async ordering; skip safely.
        if (!entry) continue;
        // Mark finalized and resolve deferred so any waiting inserts can proceed.
        entry.textDone = true;
        entry.deferred.resolve();
        // Keep removed entries so we can still use their refs after deletion.
        removedEntries.push({ key, entry });
        // Remove from active tracking map now that this dictation is finalized.
        dictationStableTextManagerMap.current.delete(key);
      }

      if (removedEntries.length === 0) return;

      // Release active map entries while preserving their IDs for the ASR Plex recorder guard.
      onDictationFinalized?.(removedEntries.map(({ key }) => key));

      stopPicklistAdvisorSession(baseId);
      const exactRemovedEntry = removedEntries.find((removedEntry) => removedEntry.key === id);
      const preferredRemovedEntry =
        exactRemovedEntry ??
        removedEntries
          .slice()
          .sort(
            (a, b) =>
              getDictationSequenceRank(b.key, baseId) - getDictationSequenceRank(a.key, baseId)
          )[0];
      // Capture fallback insertion location at finalize-time, when entries are actually removed.
      // Prefer the exact finalized id; if unavailable, use the highest sequence segment for deterministic fallback.
      mostRecentSelectionRef.current = preferredRemovedEntry.entry.selectionRef;

      const remaining = Array.from(dictationStableTextManagerMap.current.entries());
      const [, nextEntry] = remaining.sort(([a], [b]) => a.localeCompare(b))[0] ?? [];
      nextEntry?.deferred.resolve();

      logger.info('[finalizeDictation] Finalized dictation entries', {
        baseId,
        reason,
        removedEntries: removedEntries.length,
        remainingEntries: dictationStableTextManagerMap.current.size,
      });
    },
    [isReporterModifyDictationEnabled, onDictationFinalized, stopPicklistAdvisorSession]
  );

  const getSelectionRefForDictation = useCallback(
    (
      id: string,
      dictation: DictationStableTextManagerMapValue
    ): { rangeRef: RangeRef | null; recoveredFromSibling: boolean } => {
      if (dictation.selectionRef?.current != null) {
        return { rangeRef: dictation.selectionRef, recoveredFromSibling: false };
      }

      if (!isReporterModifyDictationEnabled) {
        return {
          rangeRef: mostRecentSelectionRef.current ?? null,
          recoveredFromSibling: false,
        };
      }

      const targetOffset = dictation.audioOffsetMs ?? 0;
      const { baseId, familyEntries } = getSortedDictationFamilyEntries(
        id,
        dictationStableTextManagerMap.current,
        isReporterModifyDictationEnabled
      );

      // Prefer a later sibling first because, in the regression case, that later segment still
      // has the insertion anchor we want to recover from. If none exists, fall back to the
      // nearest earlier sibling before finally falling back to the most recently finalized ref.
      const laterSibling = familyEntries.find(
        ([key, entry]) =>
          key !== id && (entry.audioOffsetMs ?? 0) >= targetOffset && entry.selectionRef?.current
      );
      const earlierSibling = familyEntries
        .slice()
        .reverse()
        .find(
          ([key, entry]) =>
            key !== id && (entry.audioOffsetMs ?? 0) < targetOffset && entry.selectionRef?.current
        );

      const recoveredEntry = laterSibling ?? earlierSibling;
      const recoveredRange = recoveredEntry?.[1].selectionRef?.current;
      if (recoveredRange == null) {
        return {
          rangeRef: mostRecentSelectionRef.current ?? null,
          recoveredFromSibling: false,
        };
      }

      replaceSelectionRef(editor, dictation, recoveredRange, { affinity: 'inward' });

      logger.warn(
        '[processStableText] Recovered missing dictation range ref from sibling segment',
        {
          id,
          recoveredFromId: recoveredEntry?.[0],
          baseId,
          audioOffsetMs: targetOffset,
          recoveredAudioOffsetMs: recoveredEntry?.[1].audioOffsetMs ?? null,
        }
      );

      return { rangeRef: dictation.selectionRef, recoveredFromSibling: true };
    },
    [editor, isReporterModifyDictationEnabled]
  );

  const markDictationStopped = useCallback(
    (id: string) => {
      // Normalize to the logical dictation id so we can mark base + any sequence segments.
      const baseId = resolveDictationFamilyId(
        id,
        dictationStableTextManagerMap.current,
        isReporterModifyDictationEnabled
      );
      const now = Date.now();
      let marked = 0;

      for (const [key, entry] of dictationStableTextManagerMap.current.entries()) {
        // Base segment (no suffix) for this dictation family.
        if (key === baseId) {
          // Preserve first stop timestamp; later stop events should not reset elapsed time.
          entry.stoppedAtMs = entry.stoppedAtMs ?? now;
          marked += 1;
          continue;
        }

        // Match only numeric modify-dictation segment suffixes: baseId-1, baseId-2, ...
        if (isNumericSegmentForBaseId(key, baseId)) {
          // Preserve first stop timestamp for the same reason as the base segment.
          entry.stoppedAtMs = entry.stoppedAtMs ?? now;
          marked += 1;
        }
      }

      logger.info('[markDictationStopped] Marked dictation as stopped', {
        id,
        baseId,
        markedEntries: marked,
        dictationCount: dictationStableTextManagerMap.current.size,
      });
    },
    [isReporterModifyDictationEnabled]
  );

  const updateSelectionRefsForDictationFamily = useCallback(
    (id: string, insertPoint: Range['anchor'], currentAudioOffsetMs?: number) => {
      const nextRange = {
        anchor: insertPoint,
        focus: insertPoint,
      };

      if (!isReporterModifyDictationEnabled) {
        const dictation = dictationStableTextManagerMap.current.get(id);
        if (dictation) {
          replaceSelectionRef(editor, dictation, nextRange);
        }
        return;
      }

      const baseId = resolveDictationFamilyId(
        id,
        dictationStableTextManagerMap.current,
        isReporterModifyDictationEnabled
      );
      const threshold = currentAudioOffsetMs ?? 0;

      // Only later-or-equal segments should move forward. Earlier segments must keep their
      // own insertion history intact so late stable-text packets can still land in the right spot.
      for (const [key, entry] of dictationStableTextManagerMap.current.entries()) {
        if (key !== baseId && !isNumericSegmentForBaseId(key, baseId)) continue;
        if ((entry.audioOffsetMs ?? 0) < threshold) continue;
        replaceSelectionRef(editor, entry, nextRange);
      }
    },
    [editor, isReporterModifyDictationEnabled]
  );

  const evictBlockedStoppedLowestDictation = useCallback(
    (triggeringId: string) => {
      // Only evict the currently blocking lowest entry. This keeps behavior focused and predictable.
      const allIds = Array.from(dictationStableTextManagerMap.current.keys()).sort();
      const lowestId = allIds[0];
      if (!lowestId || lowestId === triggeringId) return;

      const lowestEntry = dictationStableTextManagerMap.current.get(lowestId);
      const stoppedAt = lowestEntry?.stoppedAtMs ?? null;

      if (stoppedAt == null) return;

      const stoppedForMs = Date.now() - stoppedAt;
      // Give the stopped session a short grace period in case completion is still in flight.
      if (stoppedForMs < STOPPED_DICTATION_STALE_GRACE_MS) return;

      logger.warn(
        '[evictBlockedStoppedLowestDictation] Evicting blocked stopped lowest dictation entry',
        {
          triggeringId,
          entryId: lowestId,
          stoppedForMs,
        }
      );

      finalizeDictation(lowestId, FinalizeDictationReason.StaleSuperseded);
    },
    [finalizeDictation]
  );

  const waitForEarlierDictationsToFlush = useCallback(
    async (id: string) => {
      evictBlockedStoppedLowestDictation(id);

      const allIds = Array.from(dictationStableTextManagerMap.current.keys()).sort();
      const lowestId = allIds[0];

      if (!lowestId || lowestId === id) return;

      const lowestEntry = dictationStableTextManagerMap.current.get(lowestId);
      if (!lowestEntry) return;

      logger.info('[processStableText] Waiting for earlier dictation to flush before insert', {
        id,
        waitingForId: lowestId,
        dictationCount: allIds.length,
      });

      const stoppedAt = lowestEntry.stoppedAtMs;
      if (stoppedAt == null) {
        await lowestEntry.deferred.promise;
        return;
      }

      // A stopped blocker may never receive ASRPlex's final stable/done response after a
      // transport drop. Wait through the normal grace window, then finalize only that
      // blocker so newer stable text does not remain trapped behind it indefinitely.
      const stoppedForMs = Date.now() - stoppedAt;
      const remainingGraceMs = Math.max(0, STOPPED_DICTATION_STALE_GRACE_MS - stoppedForMs);
      if (remainingGraceMs > 0) {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        try {
          const waitResult = await Promise.race([
            lowestEntry.deferred.promise.then(() => 'resolved' as const),
            new Promise<'timed_out'>((resolve) => {
              timeoutId = setTimeout(() => resolve('timed_out'), remainingGraceMs);
            }),
          ]);
          if (waitResult === 'resolved') return;
        } finally {
          if (timeoutId != null) clearTimeout(timeoutId);
        }
      }

      finalizeDictation(lowestId, FinalizeDictationReason.StaleSuperseded);
    },
    [evictBlockedStoppedLowestDictation, finalizeDictation]
  );

  const postProcessStableText = useCallback(
    (id: string, dictation: DictationStableTextManagerMapValue) => {
      // check whether we can remove items from the map
      // if textDone is true, the insertion should be complete

      if (dictation.textDone) {
        finalizeDictation(id, FinalizeDictationReason.TextDone);
      }
    },
    [finalizeDictation]
  );

  const flushDeferredStepsForSelection = useCallback(
    (selection: Range, dictationId: string): boolean => {
      const queuedStepsForSelection = selectionToStepsQueueMap.current.get(selection) ?? [];
      if (queuedStepsForSelection.length === 0) return false;

      logger.info('[flushDeferredStepsForSelection] Flushing deferred queued steps for selection', {
        dictationId,
        selection: stringifyRange(selection),
        deferredStepCount: queuedStepsForSelection.length,
      });

      const dictationFamilyId = resolveDictationFamilyId(
        dictationId,
        dictationStableTextManagerMap.current,
        isReporterModifyDictationEnabled
      );
      const entryToAdvance = Array.from(dictationStableTextManagerMap.current.entries()).find(
        ([key, entry]) =>
          (key === dictationFamilyId || isNumericSegmentForBaseId(key, dictationFamilyId)) &&
          Range.equals(entry.selection, selection)
      )?.[1];

      const resolvedQueuedStepEntries = queuedStepsForSelection.map(resolveOriginalTextStepEntry);
      const groupedQueuedStepEntries = createGroupedStepEntries(resolvedQueuedStepEntries);
      let insertAt = selection;
      let lastInsertPoint: Nullable<Point> = null;
      groupedQueuedStepEntries.forEach((resolvedQueuedStepEntryList) => {
        const insertPointRef = applySteps({
          editor,
          steps: resolvedQueuedStepEntryList,
          at: insertAt,
          select: false,
          autoCorrectDictionary,
          source: slateSource.user.dictation,
          dictationId,
          dictationIndex: 0,
        });

        const insertPoint = insertPointRef?.unref();
        if (insertPoint != null) {
          lastInsertPoint = insertPoint;
          insertAt = { anchor: insertPoint, focus: insertPoint };
        }
      });

      if (entryToAdvance && lastInsertPoint != null) {
        entryToAdvance.selection = { anchor: lastInsertPoint, focus: lastInsertPoint };
        replaceSelectionRef(editor, entryToAdvance, entryToAdvance.selection, {
          affinity: 'inward',
        });
      }

      selectionToStepsQueueMap.current.delete(selection);
      removeDecorationForSelection(selection);
      setPicklistAdvisorDeferred({ dictationId: dictationFamilyId, deferred: false });
      return true;
    },
    [
      applySteps,
      autoCorrectDictionary,
      dictationStableTextManagerMap,
      editor,
      isReporterModifyDictationEnabled,
      removeDecorationForSelection,
      setPicklistAdvisorDeferred,
    ]
  );

  const processStableText = useCallback(
    async (
      msg: NvoqMessage,
      selection: Nullable<Range>,
      id: string,
      dictation: DictationStableTextManagerMapValue,
      messageId: string
    ) => {
      const dictationSource = slateSource.user.dictation;
      const activeSelection = isReporterModifyDictationEnabled ? dictation.selection : selection;
      const didSelectionMoveToNewSegment =
        isReporterModifyDictationEnabled &&
        selection != null &&
        activeSelection != null &&
        !Range.equals(selection, activeSelection);
      // If the ID of this processStableText is not the lowest ID (meaning, it's not coming from the earliest websocket),
      // there is another earlier dictation which needs to be inserted into report
      // we need to wait for the deferred control to be resolved
      await waitForEarlierDictationsToFlush(id);

      measureStableTextPerformance({ textDone: msg.data.textDone, text: msg.data.text, id });

      // Wait for the most recent stable text message before this one to finish processing.
      if (dictation.messageQueue.size) {
        let maxId = '';
        let deferred: Deferred<void> | null = null;
        for (const [key, value] of dictation.messageQueue.entries()) {
          if (key < messageId && key > maxId) {
            maxId = key;
            deferred = value;
          }
        }
        await deferred?.promise;
      }

      let mutatedMessage = autoCorrectNvoqStableText(msg, autoCorrectDictionary);

      const variant = getCurrentActiveListVariant(editor);
      mutatedMessage = generateNextParagraphInListSironaSubstitutionForNvoq(
        mutatedMessage,
        variant
      );

      const dictationChunks = createDictationChunksFromNvoqStableText(mutatedMessage);
      const hasStableTextSubstitution = dictationChunks.some(
        (dictationChunk) => dictationChunk.type === 'SUBSTITUTION'
      );
      const queuedStepsForOriginalSelection =
        (selection && selectionToStepsQueueMap.current.get(selection)) ?? [];

      if (
        isReporterModifyDictationEnabled &&
        selection &&
        activeSelection &&
        !Range.equals(selection, activeSelection) &&
        queuedStepsForOriginalSelection.length > 0
      ) {
        // User navigated to a new segment; flush any deferred commands still tied to the prior field now.
        flushDeferredStepsForSelection(selection, msg.id);
      }

      const queuedStepsForSelection =
        (activeSelection && selectionToStepsQueueMap.current.get(activeSelection)) ?? [];
      const queuedDictationChunks = queuedStepsForSelection.map((stepEntry) => stepEntry[1]);

      const fieldNames = getNavigationItemsFromEditor(editor)
        .filter((item) => isSquareBracketType(item.node.type))
        .map((item) => item.name);

      const picklistId = dictation.picklistId;
      const allowTriggerlessOptionNameMatch =
        dictation.isTriggerlessPicklistChoiceAllowed ?? isTriggerlessAllowedForPicklist(picklistId);

      const picklistOptionNames = isReporterPicklistOptionByNameEnabled
        ? getPicklistOptions(picklistId).map((option) => option.name)
        : [];
      const picklistAdvisorText =
        isReporterPicklistOptionByNameEnabled &&
        picklistId != null &&
        dictation.hasSeenStableTextSubstitution === false
          ? getPicklistAdvisorTranscript(id)
          : null;

      const stepEntries = await createStepsFromStableText(
        [...queuedDictationChunks, ...dictationChunks],
        {
          fieldNames,
          enablePicklistDictation,
          picklistOptionNames,
          picklistId,
          allowTriggerlessOptionNameMatch,
          picklistAdvisorText,
          source: dictationSource,
        }
      );

      if (hasStableTextSubstitution) {
        dictation.hasSeenStableTextSubstitution = true;
      }

      const resolvedStepEntries = didSelectionMoveToNewSegment
        ? stepEntries.map(resolveOriginalTextStepEntry)
        : stepEntries;

      // Check for deferred steps EARLY, before any async operations
      // This prevents race conditions where message 2 starts before message 1 finishes
      const deferredSteps = didSelectionMoveToNewSegment
        ? []
        : getDeferredSteps(resolvedStepEntries, {
            fieldNames,
            editor,
            picklistOptionNames,
            picklistId,
            allowTriggerlessOptionNameMatch,
          });
      // If selection already moved to a newer modify-dictation segment before this stable text arrived,
      // do not defer again; apply the text to the original segment immediately to avoid dropping it.
      if (didSelectionMoveToNewSegment && stepEntries.length > 0) {
        logger.info(
          '[processStableText] Selection moved to newer segment before stable text arrived; bypassing deferral',
          {
            id,
            originalSelection: selection && stringifyRange(selection),
            activeSelection: activeSelection && stringifyRange(activeSelection),
            stepCount: stepEntries.length,
          }
        );
      }
      const hasPendingDeferredAdvisorSteps =
        deferredSteps.length > 0 && msg.data.textDone === false;
      // Mark this dictation as deferred only while unresolved advisor steps remain
      // and nVoq has not signaled completion (`textDone`).
      setPicklistAdvisorDeferred({
        dictationId: id,
        deferred: hasPendingDeferredAdvisorSteps,
      });

      // Store deferred steps immediately if needed
      if (deferredSteps.length > 0 && msg.data.textDone === false && activeSelection) {
        selectionToStepsQueueMap.current.set(activeSelection, [...deferredSteps]);
      }

      let groupedStepEntries = createGroupedStepEntries(resolvedStepEntries);

      // if the textDone = true is received, send update the textDone = true in the metadata map
      if (msg.data.textDone === true) {
        dictation.textDone = true;
        if (activeSelection) selectionToStepsQueueMap.current.delete(activeSelection);
      }

      const stepEntriesText = stepEntriesTextToString(resolvedStepEntries);

      // if the stable text is empty, and the queued steps don't form a voice command, we don't want to do anything
      // BUT: if we have queued deferred steps, we need to process them even if the current text is empty
      if (msg.data.text === '' && stepEntriesText === '' && queuedStepsForSelection.length === 0) {
        if (activeSelection) removeDecorationForSelection(activeSelection);

        // if there is no more text to process we can go straight to postProcessStableText
        // otherwise there will be another chance after the applySteps calls later on
        postProcessStableText(id, dictation);
        return;
      }

      // if the mouse is pressed, wait for it to be released.
      if (isMousePressed === true) {
        await mouseUnpressed;
      }

      // If mouse activity happened since the previous stable-text render, only refresh
      // dictation.selectionRef when the post-mouseup selection is expanded (drag/highlight).
      // Do NOT refresh on a collapsed click-only move: in-flight dictation must stay anchored
      // to its original registered selection to avoid rerouting text to a different field.
      // Skip entirely for a late packet that belongs to an older segment: the mouseup already
      // registered a newer segment for the new selection, and speech from before the click must
      // stay where it was dictated. Leave the flag set so the newer segment still consumes it.
      if (!didSelectionMoveToNewSegment) {
        if (
          mousePressedSinceLastStableTextRender.current === true &&
          editor.selection != null &&
          Range.isExpanded(editor.selection)
        ) {
          replaceSelectionRef(editor, dictation, getEditorDictationSelectionSafe(editor), {
            affinity: 'inward',
          });
        }
        // A collapsed click inside the range this dictation is anchored to (e.g. into a generated
        // impression that was selected as a whole) means the user no longer wants that range replaced.
        if (
          mousePressedSinceLastStableTextRender.current === true &&
          editor.selection != null &&
          Range.isCollapsed(editor.selection) &&
          dictation.selectionRef.current != null &&
          Range.isExpanded(dictation.selectionRef.current) &&
          Range.includes(dictation.selectionRef.current, editor.selection.anchor)
        ) {
          replaceSelectionRef(editor, dictation, getEditorDictationSelectionSafe(editor), {
            affinity: 'inward',
          });
        }

        // reset this so we don't recalculate the selection until the next time mouse is pressed
        mousePressedSinceLastStableTextRender.current = false;
      }

      setShouldSkipWebsocketCreationRef.current != null &&
        setShouldSkipWebsocketCreationRef.current(true);

      // Adjust groupedStepEntries based on deferred steps
      if (deferredSteps.length > 0 && msg.data.textDone === false && activeSelection) {
        groupedStepEntries = createGroupedStepEntries(
          resolvedStepEntries.slice(0, -deferredSteps.length)
        );
      }

      // each partition will go through their own process of whether to do editor voice shortcut and/or applySteps.
      groupedStepEntries.forEach((stepEntryList: StepEntry[], groupIndex) => {
        const { rangeRef: mostRecentRangeRefToInsert, recoveredFromSibling } =
          getSelectionRefForDictation(id, dictation);

        if (!mostRecentRangeRefToInsert || !mostRecentRangeRefToInsert.current) {
          const rangeRefInfoMessage =
            '[processStableText] Could not find a range ref given the selection. Should correct itself in next iteration.';
          createEditorLog(rangeRefInfoMessage);
          logger.error(rangeRefInfoMessage, {
            editor: editor != null ? partialEditor(editor) : 'null',
            queuedStepsForSelection: JSON.stringify(queuedStepsForSelection),
            createdSteps: JSON.stringify(stepEntryList),
          });

          if (activeSelection) cleanMessageQueueForSelection(activeSelection, msg.data.textDone);
          return;
        }

        const firstStepEntry: StepEntry = stepEntryList[0];
        const [firstStep, firstDictationChunk]: [Step, ParsedDictationChunk] = firstStepEntry;
        const shouldAttemptEditorVoiceCommand =
          firstStep.type === 'VOICE_COMMAND' &&
          firstDictationChunk.type === 'SUBSTITUTION' &&
          firstDictationChunk.payload.target === 'EDITOR';

        const shouldAttemptExternalVoiceCommand =
          firstStep.type === 'EXTERNAL_VOICE_COMMAND' &&
          firstDictationChunk.type === 'SUBSTITUTION' &&
          firstDictationChunk.payload.target !== 'EDITOR';

        const originalTextStepEntry = (
          firstDictationChunk as ParsedDictationChunkSubstitutionEditorNextNumberDynamic
        ).payload?.originalTextStepEntry;

        let didVoiceCommand = false;

        if (shouldAttemptEditorVoiceCommand) {
          const firstPayload = firstDictationChunk.payload;
          let executedPicklistOptionSelectionPicklistId: string | null = null;

          const picklistIdOverride =
            isReporterModifyDictationEnabled && firstPayload.action === 'PicklistOptionSelection'
              ? (dictation.picklistId ??
                (firstPayload as { picklistId?: string | null }).picklistId)
              : undefined;

          // Dictation started outside a picklist but user tabbed into one before stable text
          // arrived. The PicklistOptionSelection matched against the active editor selection
          // is a false positive — skip and fall through to text insertion.
          // Exception: a navigation voice command in the same chunk (e.g. "next field pick 2")
          // rebinds selectionRef to the new picklist, so mostRecentRangeRefToInsert.current
          // will be inside a picklist in that case — not orphaned.
          const isOrphanedPicklistCommand =
            firstPayload.action === 'PicklistOptionSelection' &&
            dictation.picklistId == null &&
            getActivePicklist(editor, mostRecentRangeRefToInsert.current) == null;

          if (isOrphanedPicklistCommand) {
            stepEntryList = [originalTextStepEntry!];
          } else {
            didVoiceCommand = maybeDoVoiceCommand(
              editor,
              firstDictationChunk as ParsedDictationChunkSubstitutionEditor,
              {
                insertSelectedPicklistItemByIndex,
                ignoreMergeFieldsInNavigation,
                setMostRecentInput,
                generateAndStitchImpression,
                onInsertTextRequiredField,
                picklistIdOverride,
                onPicklistOptionSelectionExecuted: (executedPicklistId) => {
                  executedPicklistOptionSelectionPicklistId = executedPicklistId;
                },
              }
            );
          }

          if (didVoiceCommand === true) {
            enqueueDictationStepToast(
              `[dictation] - Voice command triggered: '${
                firstDictationChunk.payload.action ?? 'n/a'
              }'`
            );

            if (firstPayload.action === 'PicklistOptionSelection') {
              // Prevent future triggerless matches in this picklist after we've already made a match.
              if (executedPicklistOptionSelectionPicklistId != null) {
                consumeTriggerlessPicklistChoice(executedPicklistOptionSelectionPicklistId);
                dictation.isTriggerlessPicklistChoiceAllowed = false;
              }

              // In modify mode, keep anchor in the originating picklist and advance it to the
              // end of the inserted picklist content so trailing text from the same utterance
              // appends correctly (instead of drifting or splitting prior content).
              if (isReporterModifyDictationEnabled) {
                const picklistEnd = getPicklistEndPointById(
                  editor,
                  executedPicklistOptionSelectionPicklistId ?? picklistIdOverride ?? null
                );
                if (picklistEnd != null) {
                  replaceSelectionRef(
                    editor,
                    dictation,
                    { anchor: picklistEnd, focus: picklistEnd },
                    { affinity: 'inward' }
                  );
                }
              }
            }

            const isModifyModePicklistSelection =
              isReporterModifyDictationEnabled && firstPayload.action === 'PicklistOptionSelection';

            // Keep the original anchor for picklist selection commands in modify mode so
            // trailing text from the same stable-text packet does not drift
            // after tab/modify. Other command paths keep existing behavior.
            const shouldRebindAnchorToCurrentSelection =
              editor.selection != null && !isModifyModePicklistSelection;

            if (shouldRebindAnchorToCurrentSelection) {
              replaceSelectionRef(editor, dictation, getEditorDictationSelectionSafe(editor), {
                affinity: 'inward',
              });
            }

            // With modify dictation, refresh picklist context after navigation so next PicklistOptionSelection in same chunk targets the new field
            if (
              isReporterModifyDictationEnabled &&
              (firstPayload.action === 'NextBracket' ||
                firstPayload.action === 'PreviousBracket' ||
                firstPayload.action === 'GoToField')
            ) {
              refreshDictationPicklistContext(editor, dictation);
            }
          } else if (didVoiceCommand === false && originalTextStepEntry != null) {
            // if the voice command was not successful, we want to apply the text insertion from the originalTextStepEntry
            stepEntryList = [originalTextStepEntry];
          }
        } else if (shouldAttemptExternalVoiceCommand && activeSelection) {
          onExternalSubstitution({
            type: 'RecorderVoiceCommand',
            selection: activeSelection,
            substitution: firstDictationChunk.payload,
          });

          if (firstDictationChunk.payload.action === 'InsertReportTemplate') {
            removeDecorationForSelection(activeSelection);
          }
        }

        // If this command is a dictation segment boundary, increment the dictation index.
        let currentDictationIndex = dictation.dictationIndex;
        if (
          firstStep.type === 'INSERT_MACRO' ||
          (didVoiceCommand &&
            firstDictationChunk.type === 'SUBSTITUTION' &&
            VOICE_COMMAND_TYPES_EDITOR.includes(
              firstDictationChunk.payload.action as VoiceCommandsEditor
            ))
        ) {
          dictation.dictationIndex = ++currentDictationIndex;
        }

        if (activeSelection) cleanMessageQueueForSelection(activeSelection, msg.data.textDone);

        let stepEntriesString = stepEntriesTextToString(stepEntryList);
        if (
          (isAlwaysEditable ||
            REPORT_EDITABLE_STATUSES.includes(
              reportStatus as (typeof REPORT_EDITABLE_STATUSES)[number]
            )) &&
          didVoiceCommand === false
        ) {
          const selectedRange = getRangeRefSafe(mostRecentRangeRefToInsert);
          // Resolve case only after macro lookup and selection recovery, without replaying the packet.
          if (
            groupIndex === 0 &&
            queuedStepsForSelection.length === 0 &&
            firstStep.type === 'INSERT_TEXT' &&
            firstDictationChunk.type === 'TEXT'
          ) {
            const contextualStep = getContextualLeadingTextStep({
              editor,
              at: selectedRange,
              message: msg,
              formattedMessage: mutatedMessage,
              sourceChunk: firstDictationChunk,
              dictionary: autoCorrectDictionary,
              headingLabelSet,
              variant,
            });
            if (contextualStep != null) {
              stepEntryList = [contextualStep, ...stepEntryList.slice(1)];
              stepEntriesString = stepEntriesTextToString(stepEntryList);
            }
          }
          const replacementText = stepEntriesString.trim();
          const isSelectedTextBeingReplaced =
            selectedRange != null && !Range.isCollapsed(selectedRange) && replacementText !== '';
          // tracks manual corrections of a few words
          if (isSelectedTextBeingReplaced) {
            const [node] = Node.fragment(editor, selectedRange);
            const selectedText = Node.string(node);
            if (wordCount(selectedText) <= MANUAL_CORRECTION_WORDS_LIMIT) {
              analytics.track(reporter.sys.textCorrectedByDictation, {
                selectedText,
                selectedTextWordCount: wordCount(selectedText),
                selectedTextCharacterCount: selectedText.length,
                replacementText,
                replacementTextWordCount: wordCount(replacementText),
                replacementTextCharacterCount: replacementText.length,
              });
            }
          }
          const editorDictationSelectionSafe = getEditorDictationSelectionSafe(editor);

          logger.info(
            `[processStableText] StableText: "${stepEntriesTextToString(stepEntryList)}". Applying ${
              stepEntryList.length
            } steps to editor.`,
            {
              logId: selection && stringifyRange(selection),
              resolvedSelection: activeSelection && stringifyRange(activeSelection),
              editor: partialEditor(editor),
              steps: stepEntryList,
              stepsText: stepEntriesTextToString(stepEntryList),
              at: selectedRange,
              select: Range.includes(selectedRange, getEditorDictationSelectionSafe(editor)),
            }
          );

          const stepEntriesWordCount = wordCount(stepEntriesString);
          if (stepEntriesWordCount > 0) {
            analytics.track(reporter.usr.wordsDictated, {
              ...analyticsData,
              dictatedWords: stepEntriesString,
              dictatedWordCount: stepEntriesWordCount,
              dictatedCharacterCount: stepEntriesString.length,
            });
          } else {
            logger.info(
              '[processStableText] processStableText returned no dictated words for selection',
              {
                logId: selection && stringifyRange(selection),
                resolvedSelection: activeSelection && stringifyRange(activeSelection),
                msg,
                editor: editor != null ? partialEditor(editor) : 'null',
              }
            );
          }

          const insertPointRef = applySteps({
            editor,
            steps: stepEntryList,
            at: selectedRange,
            /**
             * If the at range includes the current editor selection, augment the editor selection,
             * otherwise leave it as is because that means they have tabbed to a future bracket
             * and this one is a previous queue item.
             *
             * NOTE: This function is named funky in Slate. See:
             * https://github.com/ianstormtaylor/slate/issues/4283
             */
            select: Range.includes(selectedRange, editorDictationSelectionSafe),
            autoCorrectDictionary,
            source: dictationSource,
            dictationId: msg.id,
            dictationIndex: currentDictationIndex,
          });

          // If the stitching process occurred, update the dictation range ref
          if (insertPointRef) {
            let insertPoint = insertPointRef.unref();
            if (!insertPoint) {
              const fallbackSelection = getEditorDictationSelectionSafe(editor);
              insertPoint = Range.isCollapsed(fallbackSelection)
                ? fallbackSelection.anchor
                : Editor.end(editor, fallbackSelection);

              logger.warn('Lost insertion point after dictation stitching', {
                editor: partialEditor(editor),
                stepEntriesString,
                selectedRange,
                recoveredInsertPoint: insertPoint,
              });
            }
            if (insertPoint) {
              if (recoveredFromSibling) {
                // When we recovered from a sibling segment, advance later segments too so
                // subsequent stable text stays ordered after this recovered insertion.
                updateSelectionRefsForDictationFamily(id, insertPoint, dictation.audioOffsetMs);
              } else {
                // Set the new (collapsed) dictation range ref immediately after the site of the stitched content.
                replaceSelectionRef(editor, dictation, {
                  anchor: insertPoint,
                  focus: insertPoint,
                });
              }
            }
          }

          if (picklistId != null && stepEntriesString.trim() !== '') {
            // Disable triggerless matching for a picklist once regular text has been stitched into it.
            consumeTriggerlessPicklistChoice(picklistId);
            dictation.isTriggerlessPicklistChoiceAllowed = false;
          }

          // With modify dictation, refresh picklist context after InsertMacro so next PicklistOptionSelection targets the field at the new insert point
          if (
            isReporterModifyDictationEnabled &&
            stepEntryList.some((entry) => entry[0].type === 'INSERT_MACRO')
          ) {
            refreshDictationPicklistContext(editor, dictation);
          }
        }

        mostRecentStableTextInserted.current = stepEntriesString;
      });
      // go to postProcessStableText, to give a chance for the dictationStableTextManagerMap to be updated
      postProcessStableText(id, dictation);
    },
    [
      autoCorrectDictionary,
      headingLabelSet,
      editor,
      createStepsFromStableText,
      enablePicklistDictation,
      isMousePressed,
      cleanMessageQueueForSelection,
      reportStatus,
      isAlwaysEditable,
      insertSelectedPicklistItemByIndex,
      ignoreMergeFieldsInNavigation,
      setMostRecentInput,
      generateAndStitchImpression,
      onInsertTextRequiredField,
      enqueueDictationStepToast,
      onExternalSubstitution,
      removeDecorationForSelection,
      applySteps,
      analyticsData,
      mouseUnpressed,
      isReporterModifyDictationEnabled,
      isReporterPicklistOptionByNameEnabled,
      postProcessStableText,
      waitForEarlierDictationsToFlush,
      getPicklistOptions,
      isTriggerlessAllowedForPicklist,
      consumeTriggerlessPicklistChoice,
      setPicklistAdvisorDeferred,
      getPicklistAdvisorTranscript,
      getSelectionRefForDictation,
      updateSelectionRefsForDictationFamily,
      flushDeferredStepsForSelection,
    ]
  );
  const processStableTextSerially = useCallback(
    async (msg: NvoqMessage, selection: Range, id: string) => {
      // Create a deferred for subsequent stable text messages in the same dictation to wait on.
      const deferred = new Deferred<void>();

      let dictation: Nullable<DictationStableTextManagerMapValue> = null;
      // using modifyDictation, this is under the assumption that any stable text returned will be placed in the same position in its entirety.
      // without it, this won't work properly, as text content may have to be placed into different selectionRefs.
      if (isReporterModifyDictationEnabled) {
        const markerArray = msg.data.markers ?? [];
        const firstMarker = markerArray[0];
        const lastMarker = markerArray[markerArray.length - 1];

        const markerStart = firstMarker ? getMarkerStartMs(firstMarker) : 0;
        const markerEnd = lastMarker ? getMarkerEndMs(lastMarker) : 0;

        // Trailing punctuation belongs to the text it follows, so route it by
        // onset: its marker can straddle a modify boundary and overlap would push
        // it into the next field. Words keep the existing overlap routing.
        const closestMapEntryKey = isTrailingPunctuationText(msg.data.text ?? '')
          ? getEntryIdForOnset(dictationStableTextManagerMap.current, markerStart)
          : getClosestEntryId(dictationStableTextManagerMap.current, markerStart, markerEnd);
        if (closestMapEntryKey) {
          dictation = dictationStableTextManagerMap.current.get(closestMapEntryKey);
        }
      } else {
        dictation = dictationStableTextManagerMap.current.get(id);
      }

      if (!dictation) {
        logger.error(`[processStableTextSerially] No existing dictation entry found for id ${id}`);
        return;
      }
      const messageId = uuidv7();

      // Place it in the dictationStableTextManagerMap messageQueue.
      dictation.messageQueue.set(messageId, deferred);

      try {
        await processStableText(msg, selection, id, dictation, messageId);
      } finally {
        // Resolve this message's promise so that the next message can be processed.
        deferred.resolve();
      }
    },
    [processStableText, isReporterModifyDictationEnabled]
  );

  const skipWebsocketCreationOptionsRef = useRef<Nullable<WebsocketCreationOptions>>();

  const processHypothesisText = useCallback(
    (msg: NvoqMessage, selection: Range, id: string) => {
      measureHypothesisTextPerformance({
        text: msg.data.text,
        id,
      });

      // if the mouse pressed, user could be highlighting text
      // don't let hypothesis text interfere with this
      if (isMousePressed === true) return;

      let hypothesisSelection = selection;

      // Provide the most up-to-date dictation selection
      if (editor.selection != null) {
        hypothesisSelection = editor.selection;
      }
      try {
        if (Range.isCollapsed(selection) && isCursorAfterFinalHeadingColon(editor, selection)) {
          const nextInlineParagraphEntry = getOrInsertNextInlineParagraph(editor, selection);
          if (nextInlineParagraphEntry != null) {
            const inlineParagraphStart = Editor.start(editor, nextInlineParagraphEntry[1]);
            hypothesisSelection = {
              anchor: inlineParagraphStart,
              focus: inlineParagraphStart,
            };
          }
        }
      } catch (e: any) {
        const headingErrorMessage =
          '[processHypothesisText] Error occurred when rendering hypothesis text when dictating after colon in heading';
        logger.warn(headingErrorMessage, {
          selection: stringifyRange(selection),
          msg: msg.data.text,
          editor: partialEditor(editor),
        });
      }

      // We use the presence of an entry in the selectionToStepsQueueMap as a signal that we should update our state for hypothesis-text decorations.
      if (selectionToStepsQueueMap.current.has(selection)) {
        const newHypothesisText = msg.data.text;
        const stableText = mostRecentStableTextInserted.current;
        let trimmedHypothesisText = newHypothesisText;
        if (stableText !== null && !!stableText) {
          /* We will allow for a possibly "untrimmed" hypothesis text--and thus a visible decoration--if there is a non-empty
           * stable text. In fact, this is not the stable text itself, but the `stepEntriesString`, which may be empty for newlines or voice commands.
           * In such cases, we will consider the hypothesis text "trimmed" (or converged with the stable text) and *not* render the hypothesis-text decoration.
           */
          trimmedHypothesisText =
            stableText === '' ? '' : trimHypothesisText(newHypothesisText, stableText);
        }

        // Add a new entry representing the hypothesis-text decoration for this selection, if one doesn't exist in decorations state array.
        decorateDispatch((decorations) => {
          let entryExists = false;
          const newDecorations = decorations.map((d) => {
            if (d.selectionRef === selection) {
              entryExists = true;
              return { ...d, hypothesisText: trimmedHypothesisText };
            } else return d;
          });
          if (!entryExists) {
            newDecorations.push({
              anchor: hypothesisSelection.anchor,
              focus: hypothesisSelection.focus,
              selectionRef: selection,
              [DICTATION_PLUGIN_ID]: true,
              hypothesisText: trimmedHypothesisText,
            });
          }
          return newDecorations;
        });
      }
    },
    [isMousePressed, editor, decorateDispatch]
  );

  // Create a range ref that will keep the user's intended target for this dictation
  // in sync with changes to the document.
  const registerDictation = useCallback(
    (id: string, { selection, audioOffsetMs = 0, localSequence = 0 }: RegisterDictationOptions) => {
      const picklistEntry = getActivePicklist(
        editor,
        selection
      ) as NodeEntry<PicklistPluginElement> | null;
      const picklistId = picklistEntry?.[0]?.picklistID ?? null;
      refreshTriggerlessPicklistStateFromSelection(selection);
      const isTriggerlessPicklistChoiceAllowed = isTriggerlessAllowedForPicklist(picklistId);
      if (picklistId != null && isReporterPicklistOptionByNameEnabled) {
        startPicklistAdvisorSession({ dictationId: id, picklistId });
      }

      let augmentedId;

      if (isReporterModifyDictationEnabled) {
        // Using modifyDictation, we may have multiple segments for a single dictation,
        // each identified by a local sequence number.
        augmentedId = localSequence === 0 ? id : `${id}-${localSequence}`;
      } else {
        // Without modifyDictation, we only ever have one segment per dictation.
        augmentedId = id;
      }

      dictationStableTextManagerMap.current.get(augmentedId)?.selectionRef.unref();
      const selectionRef = Editor.rangeRef(editor, selection, { affinity: 'inward' });

      dictationStableTextManagerMap.current.set(augmentedId, {
        selection,
        selectionRef,
        textDone: false,
        deferred: new Deferred<void>(),
        messageQueue: new Map(),
        dictationIndex: 0,
        audioOffsetMs,
        stoppedAtMs: null,
        picklistId,
        isTriggerlessPicklistChoiceAllowed,
        hasSeenStableTextSubstitution: false,
      });

      const dictationCount = dictationStableTextManagerMap.current.size;
      logger.info(
        `[registerDictation] Registered dictation with id ${id}. Total dictations: ${dictationCount}`,
        { augmentedId, selection, selectionRef, audioOffsetMs, localSequence, picklistId }
      );
    },
    [
      editor,
      isReporterModifyDictationEnabled,
      isReporterPicklistOptionByNameEnabled,
      isTriggerlessAllowedForPicklist,
      refreshTriggerlessPicklistStateFromSelection,
      startPicklistAdvisorSession,
    ]
  );

  return {
    processStableTextSerially,
    processHypothesisText,
    processFocusMapAppend,
    processFocusMapDelete,
    registerDictation,
    flushDeferredStepsForSelection,
    markDictationStopped,
    dictationStableTextManagerMap,
    selectionToStepsQueueMap,
    mostRecentStableTextInserted,
    isMousePressed,
    setShouldSkipWebsocketCreationRef,
    skipWebsocketCreationOptionsRef,
  };
};
